import { RequestConfig } from 'packages/http-client/fetcher';
import { FetcherContext } from 'packages/http-client/react';
import { AnalyticsEvent, useSendAnalytics } from 'packages/react-analytics';
import {
  createElement,
  FC,
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';

// Utilities for beacon-like requests, based on:
// apps/acp/micro-frontends/dashboard/implementation/v1/hooks/use-dashboard-analytics.tsx

// Unsafe private hook to allowing sending a beacon-like request from fetcher
const useFetcherAsBeacon = () => {
  const fetcher = useContext(FetcherContext);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const sendEvent = useCallback(
    (requestConfig: RequestConfig) => {
      const [request] = fetcherRef.current(requestConfig);
      // Immediately fire request and ignore result
      request();
    },
    [fetcherRef]
  );
  return sendEvent;
};

// Private hook know when a component has become visible
const useWhenVisible = (): [FC, boolean] => {
  const [isComponentVisible, setComponentVisible] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // clean up intersection observer on un-mount
  useEffect(
    () => () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    },
    []
  );

  const setRef = useCallback((node) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }
    if (node !== null) {
      let observer: IntersectionObserver | null = null;
      const intersectionHandler = (en: IntersectionObserverEntry[]) => {
        if (en[0] && en[0].intersectionRatio >= 1) {
          setComponentVisible(true);
          if (observer) observer.disconnect();
        }
      };
      observer = new IntersectionObserver(intersectionHandler, {
        threshold: 1
      });
      observer.observe(node);
      observerRef.current = observer;
    }
  }, []);

  const ComponentMayBeVisible: FC = (props) => (
    <div ref={setRef} {...props}></div>
  );
  return [ComponentMayBeVisible, isComponentVisible];
};

/**
 * This hook is responsible for creating a callback
 * that should be called when the user creates a tracking event
 *
 * It also returns a function to wrap
 * an existing onClick handler as typically you want
 * to use another onClick handler, such as in useAnchor
 */

export function useTrackEvent<T = void>(
  requestConfig: RequestConfig | null,
  analyticsEvent: AnalyticsEvent | null
) {
  const sendAnalytics = useSendAnalytics();
  const sendAnalyticsRef = useRef(sendAnalytics);
  sendAnalyticsRef.current = sendAnalytics;

  const request = useFetcherAsBeacon();
  const requestRef = useRef(request);
  requestRef.current = request;

  const requestConfigRef = useRef(requestConfig);
  requestConfigRef.current = requestConfig;

  const analyticsEventRef = useRef(analyticsEvent);
  analyticsEventRef.current = analyticsEvent;

  return useCallback(
    (defaultHandler?: (ev: T) => void) => (ev: T) => {
      if (requestConfigRef.current) {
        requestRef.current(requestConfigRef.current);
      }
      if (analyticsEventRef.current) {
        sendAnalyticsRef.current(analyticsEventRef.current);
      }
      if (defaultHandler) {
        defaultHandler(ev);
      }
    },
    []
  );
}

/**
 * The component version of `useTrackEvent`
 * This allows to auto tracking of an event when the component
 * is visible. it will only log 0 or 1 times while the component
 * is mounted
 */
const TrackWhenVisibleWithAnalytics: FC<{
  requestConfig?: RequestConfig;
  analyticsEvent?: AnalyticsEvent;
}> = ({ requestConfig = null, analyticsEvent = null, children }) => {
  const [ComponentMayBeVisible, hasEverBeenVisible] = useWhenVisible();

  const track = useTrackEvent(requestConfig, analyticsEvent)();
  const trackRef = useRef(track);
  trackRef.current = track;

  useEffect(() => {
    if (hasEverBeenVisible) {
      trackRef.current();
    }
  }, [hasEverBeenVisible]);

  return (
    <Fragment>
      <ComponentMayBeVisible />
      {children}
    </Fragment>
  );
};

/**
 * The component version of `useTrackEvent`
 * This allows to auto tracking of an event when the component
 * is visible. it will only log 0 or 1 times while the component
 * is mounted
 * if browser doesn't support IntersectionObserver then analytics
 * wont be collected
 */
export const TrackWhenVisible: FC<{
  requestConfig?: RequestConfig;
  analyticsEvent?: AnalyticsEvent;
}> = ({ requestConfig, analyticsEvent, children }) => {
  let isIntersectionObserverAvailable = false;
  if ('IntersectionObserver' in window) {
    isIntersectionObserverAvailable = true;
  }

  return (
    <Fragment>
      {isIntersectionObserverAvailable ? (
        <TrackWhenVisibleWithAnalytics
          analyticsEvent={analyticsEvent}
          requestConfig={requestConfig}
        >
          {children}
        </TrackWhenVisibleWithAnalytics>
      ) : (
        children
      )}
    </Fragment>
  );
};

// Hook to create a callback to make beacon-like requests
export const useSendEventRequest = () => {
  const request = useFetcherAsBeacon();
  const requestRef = useRef(request);

  return useCallback((requestConfig: RequestConfig) => {
    if (requestRef.current) {
      requestRef.current(requestConfig);
    }
  }, []);
};
